<template>
    <div class="job job-card" v-if="job">
        <div class="card-header d-flex px-0">
            <h3 class="job-title my-1 eesc-blue" 
                :aria-label="isOfficialType(job) || isCataType(job) ? job?.jobTitle : $t(`${job.applicationType.resourceKey}`)"
            >
                {{
                    isOfficialType(job) || isCataType(job)
                        ? job?.jobTitle
                        : $t(`${job.applicationType.resourceKey}`)
                }}
            </h3>
            <span class="job-location text-center text-eesc-blue-400 ms-auto" aria-label="Brussels">
                <font-awesome-icon icon="fas fa-map-marker-alt" class="fs-4 me-2"/>
                Brussels
            </span>
        </div>

        <div class="card-body p-0">
            <div
                v-if="job.applicationType.code === 'TRAINEE'"
                class="job-details col-sm-6"
            >
                <p v-if="positionData" class="eesc-blue">
                    <font-awesome-icon v-if="positionData.cardName" :icon="positionData.icon" class="me-2"/>
                    <span  :aria-label="positionData.cardName">
                        {{ positionData.cardName }}
                    </span>
                </p>
                <p
                    v-if="
                        $te(
                            `lib_${job.applicationType.code.toLowerCase()}_period`
                        )
                    "
                    class="job-detail"
                >
                    <font-awesome-icon icon="fas fa-calendar-check" class="job-detail-icon me-2"/>
                    <span class="job-detail-text">
                        {{
                            $t(
                                `lib_${job.applicationType.code.toLowerCase()}_period`,
                                [
                                    formatDate(
                                        job.cycleStartDate,
                                        'do MMMM yyyy'
                                    ),
                                    formatDate(
                                        job.cycleEndDate,
                                        'do MMMM yyyy'
                                    ),
                                ]
                            )
                        }}
                    </span>
                </p>
                <p
                    v-if="
                        $te(
                            `${job.applicationType.code.toLowerCase()}_info_paid`
                        )
                    "
                    class="job-detail"
                >
                    <font-awesome-icon icon="fas fa-money-bill-wave" class="job-detail-icon me-2"/>
                    <span class="job-detail-text">
                        {{
                            $t(
                                `${job.applicationType.code.toLowerCase()}_info_paid`,
                                [job.basicGrantAmount]
                            )
                        }}
                    </span>
                </p>
                <p
                    v-if="
                        $te(
                            `${job.applicationType.code.toLowerCase()}_info_pref`
                        )
                    "
                    class="job-detail"
                >
                    <font-awesome-icon icon="fas fa-ballot" class="job-detail-icon me-2"/>
                    <span class="job-detail-text">
                        {{
                            $t(
                                `${job.applicationType.code.toLowerCase()}_info_pref`
                            )
                        }}
                    </span>
                </p>
                <p
                    v-if="
                        $te(
                            `${job.applicationType.code.toLowerCase()}_info_insurance`
                        )
                    "
                    class="job-detail mb-0"
                >
                    <font-awesome-icon icon="fas fa-medkit" class="job-detail-icon me-2"/>
                    <span class="job-detail-text">
                        {{
                            $t(
                                `${job.applicationType.code.toLowerCase()}_info_insurance`
                            )
                        }}
                    </span>
                </p>
                <p class="job-detail mb-1" v-if="job?.unitId">
                    <font-awesome-icon icon="fad fa-university" class="job-detail-icon me-2"/>
                    <span class="job-detail-text">
                        {{ unitDisplayName(job?.unitId) }}
                    </span>
                </p>
            </div>
            <div v-else class="job-details col-sm-6">
                <p v-if="positionData" class="eesc-blue">
                    <font-awesome-icon v-if="positionData.cardName" :icon="positionData.icon" class="me-2"/>
                    <span  :aria-label="positionData.cardName">
                        {{ positionData.cardName }}
                    </span>
                </p>
                <p
                    v-if="
                        $te(
                            `${job.applicationType.code.toLowerCase()}_info_who_${currentSite.toLowerCase()}`
                        )
                    "
                    class="job-detail"
                >
                    <font-awesome-icon icon="fa fa-graduation-cap" class="job-detail-icon me-2"/>
                    <span class="job-detail-text">
                        {{
                            $t(
                                `${job.applicationType.code.toLowerCase()}_info_who_${currentSite.toLowerCase()}`
                            )
                        }}
                    </span>
                </p>
                <p
                    v-if="
                        $te(
                            `${job.applicationType.code.toLowerCase()}_info_period_${currentSite.toLowerCase()}`
                        )
                    "
                    class="job-detail"
                >
                    <font-awesome-icon icon="fas fa-calendar-check" class="job-detail-icon me-2"/>
                    <span class="job-detail-text">
                        {{
                            $t(
                                `${job.applicationType.code.toLowerCase()}_info_period_${currentSite.toLowerCase()}`
                            )
                        }}
                    </span>
                </p>
                <p
                    v-if="
                        ( $te(
                            `${job.applicationType.code.toLowerCase()}_info_paid_${currentSite.toLowerCase()}`
                        ) && currentSite.toLowerCase() == 'eesc' )
                        ||
                        ( $te(
                            `${job.applicationType.code.toLowerCase()}_info_unpaid_${currentSite.toLowerCase()}`
                        ) && currentSite.toLowerCase() == 'cor' )
                    "
                    class="job-detail"
                >
                    <font-awesome-icon icon="fas fa-money-bill-wave" class="job-detail-icon me-2"/>
                    <span class="job-detail-text" v-if="currentSite.toLowerCase() == 'eesc'">
                        {{
                            $t(
                                `${job.applicationType.code.toLowerCase()}_info_paid_${currentSite.toLowerCase()}`,
                                [job.basicGrantAmount]
                            )
                        }}
                    </span>
                    <span class="job-detail-text" v-if="currentSite.toLowerCase() == 'cor'">
                        {{
                            $t(
                                `${job.applicationType.code.toLowerCase()}_info_unpaid_${currentSite.toLowerCase()}`
                            )
                        }}
                    </span>
                </p>
                <p
                    v-if="
                        $te(
                            `${job.applicationType.code.toLowerCase()}_info_hours_${currentSite.toLowerCase()}`
                        )
                    "
                    class="job-detail"
                >
                    <font-awesome-icon icon="fas fa-hourglass" class="job-detail-icon me-2"/>
                    <span class="job-detail-text">
                        {{
                            $t(
                                `${job.applicationType.code.toLowerCase()}_info_hours_${currentSite.toLowerCase()}`
                            )
                        }}
                    </span>
                </p>
                <p
                    v-if="
                        $te(
                            `${job.applicationType.code.toLowerCase()}_info_insurance_${currentSite.toLowerCase()}`
                        )
                    "
                    class="job-detail mb-0"
                >
                    <font-awesome-icon icon="fas fa-medkit" class="job-detail-icon me-2"/>
                    <span class="job-detail-text">
                        {{
                            $t(
                                `${job.applicationType.code.toLowerCase()}_info_insurance_${currentSite.toLowerCase()}`
                            )
                        }}
                    </span>
                </p>
                <p
                    class="job-detail mb-1"
                    v-if="isOfficialType(job) || isCataType(job)"
                >
                    <font-awesome-icon icon="fas fa-signature" class="job-detail-icon me-2"/>
                    <span class="job-detail-text">
                        {{ job.name }}
                    </span>
                </p>              
                <p
                    class="job-detail mb-0"
                    v-if="isOfficialType(job)"
                >
                    <font-awesome-icon icon="fad fa-diploma" class="job-detail-icon me-2"/>
                    <span class="job-detail-text">
                        {{ functionLabel(job?.function) }},
                        {{ job?.minGrade }} -
                        {{ job?.maxGrade }}
                    </span>
                </p>
                <p
                    class="job-detail mb-0"
                    v-if="isCataType(job)"
                >
                    <font-awesome-icon icon="fad fa-diploma" class="job-detail-icon me-2"/>
                    <span class="job-detail-text">
                        {{ functionLabel(job?.function) }},
                        {{ job?.minGrade }}
                    </span>
                </p>
                <p class="job-detail mb-1" v-if="job?.unitId">
                    <font-awesome-icon icon="fad fa-university" class="job-detail-icon me-2"/>
                    <span class="job-detail-text">
                        {{ unitDisplayName(job?.unitId) }}
                    </span>
                </p>
                <p
                    v-if="isOfficialType(job)"
                    class="job-detail mb-1"
                >
                    <font-awesome-icon icon="fad fa-user-tie" class="job-detail-icon me-2"/>
                    <span class="job-detail-text">
                        <!-- {{ $t(`official_info_req`) }} -->
                        {{ applicantCategoryLabel(job.applicantCategory) }}
                    </span>
                </p>               
                <p
                    class="job-contract-dates mb-1"
                    v-if="isCataType(job)"
                >
                    <font-awesome-icon icon="fa-solid fa-calendar" class="job-detail-icon me-2"/>
                    <span class="job-contract-dates-text">
                        {{ job?.contractDuration }}
                    </span>
                </p>
                <p
                    class="job-position-type-detail mb-1"
                    v-if="isCataType(job)"
                >
                    <font-awesome-icon icon="fad fa-user-tie" class="job-detail-icon me-2"/>
                    <span class="job-position-type-detail-text">
                        {{
                            $t(
                                `${job.applicationType.code.toLowerCase()}_info_position_type`
                            )
                        }}
                    </span>
                </p>
            </div>
            <div
                class="job-update-actions col-sm-6 d-flex flex-column align-items-end justify-content-between"
            >
                <div class="job-registration-end-date mb-2" v-if="job?.endDate">
                    {{
                        registrationEndDateString
                    }}
                </div>
                <div
                    class="job-actions d-flex align-items-end justify-content-end mt-auto"
                >
                    <button
                        v-if="job.canImport"
                        type="button"
                        @click="openImportDialog"
                        class="btn btn-lg btn-outline-secondary import-btn px-4 py-1"
                    >
                        {{ $t('btn_import') }}
                    </button>
                    <button
                        @click.prevent="
                            applyToJob(job.id, job.applicationType.code)
                        "
                        class="btn btn-lg btn-primary eesc-blue apply-btn px-4 py-1"
                    >
                        {{ $t('btn_apply') }}
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import CommonMixins from '../mixins';
import { mapGetters, mapActions } from 'vuex';
export default {
    name: 'OpeningCard',
    components: {},
    props: {
        job: {
            required: true,
        },
        positionData: {
            required: false
        }
    },
    data() {
        return {};
    },
    computed: {
        ...mapGetters([
            'unitDisplayName',
            'applicantCategoryLabel',
            'currentSite',
            'selectedLanguage',
            'functionLabel',
        ]),
        registrationEndDateString(){
            console.log(this.selectedLanguage)
            let formattedDate = this.formatDateLocale(this.job?.endDate);
            let separator = this.selectedLanguage === 'en' ? '-' : 'à';
            let endTime = this.selectedLanguage === 'en'        ?
                this.job.isMiddayEndDate ? '12:00' : '24:00'    :
                this.job.isMiddayEndDate ? '12h00' : '24h00'

            return `${this.$t('lib_register_unitl')} ${formattedDate} ${separator} ${endTime} `
        }
    },
    methods: {
        ...mapActions(['createApplication']),
        isOfficialType(job) {
            return job?.applicationType?.code?.toLowerCase() === 'official';
        },
        isCaType(job) {
            return job?.applicationType?.code?.toLowerCase() === 'ca';
        },
        isTaType(job) {
            return job?.applicationType?.code?.toLowerCase() === 'ta';
        },
        isCataType(job) {
            return this.isCaType(job) || this.isTaType(job);
        },
        applyToJob(applicationCycleId, applicationType) {
            this.loading = true;
            this.createApplication({
                applicationCycleId: applicationCycleId,
            }).then(
                (applicationId) => {
                    location.href = `${location.origin}/${this.selectedLanguage}/wizard?id=${applicationId}&type=${applicationType}`;
                },
                (error) => {
                    this.loading = false;
                    this.$toast.add({
                        severity: 'error',
                        detail: error.title,
                        life: 3000,
                    });
                }
            );
        },
        openImportDialog() {
            this.$emit('openImportDialog');
        },
    },

    mounted() {},
    watch: {},
    mixins: [CommonMixins],
};
</script>
